import { Millwright } from '@/apis/millwrights/types/millwrightsApi';
import {
  Avatar,
  Icon,
  Stack,
  Tooltip,
  Typography,
  useWindowSize,
} from '@data-products-and-ai/react-components';
import { FC, useMemo } from 'react';
import { useLocation, useNavigate, generatePath, useParams } from 'react-router';
import classnames from 'classnames';

import { formatDateString, getRiskColor, getRiskLabel } from '@/utils';
import {
  DataItem,
  DataItems,
  LabeledIcon,
  SemiCircleProgressBar,
} from '@/components';
import { getUSStateName } from '@/types';
import { TABLET_WIDTH } from '@/constants';
import { MillwrightOutages } from '@/pages/Millwrights/components';
import { MILLWRIGHT_ROUTE } from '@/pages/Millwrights/routes';
import { OUTAGE_MILLWRIGHT_ROUTE } from '@/pages/Outages/routes';

import '@/pages/Millwrights/components/MillwrightCard/MillwrightCard.scss';

const MILLWRIGHT_TOOLTIP_TEXT = `The risk level shown here is relative and updated daily.

Each millwright is compared with all millwrights currently active across all outages. Based on this comparison, every millwright is assigned to a risk category — from Extreme to Low.

This normalization ensures that all risk categories are represented each day, helping identify which millwrights carry the highest relative risk at that time.`;

interface MillwrightCardProps {
  millwright: Millwright;
  clickable?: boolean;
}

export const MillwrightCard: FC<MillwrightCardProps> = ({
  millwright,
  clickable = true,
}) => {
  const { outageId } = useParams();
  const navigate = useNavigate();
  const routerLocation = useLocation();
  const { width } = useWindowSize();
  const isSmallScreen = width < TABLET_WIDTH;
  const state = getUSStateName(millwright.state);
  const locationText = `${millwright.city}, ${state}`;

  const classNames = classnames('millwright-card', {
    'millwright-card--clickable': clickable,
  });

  const dataItems = useMemo(
    () => [
      {
        key: 'id',
        label: 'Employee Nr.',
        inline: true,
        single: true,
        value: (
          <Typography tag="textsmall_strong">{millwright.empId}</Typography>
        ),
      },
      {
        key: 'jobTitleEx',
        label: 'Job title',
        single: true,
        inline: true,
        value: millwright?.jobTitleEx ?? 'N/A',
      },
      {
        key: 'rt',
        label: 'Regular time (RT)',
        value: `${millwright?.rt ?? 0} hours`,
      },
      {
        key: 'ot',
        label: 'Overtime (OT)',
        value: `${millwright?.ot ?? 0} hours`,
      },
      {
        key: 'dt',
        label: 'Double time (DT)',
        value: `${millwright?.dt ?? 0} hours`,
      },
      {
        key: 'total',
        label: 'Total time',
        value: `${millwright?.total ?? 0} hours`,
      },
      {
        key: 'state',
        label: 'State',
        single: true,
        hideLabel: true,
        value: <LabeledIcon icon="IconHome" label={locationText} />,
      },
    ],
    [millwright, locationText],
  );

  const handleClick = () => {
    const millwrightId = millwright.empId?.toString();

    navigate(
      outageId
        ? generatePath(OUTAGE_MILLWRIGHT_ROUTE, {
            outageId,
            millwrightId,
          })
        : generatePath(MILLWRIGHT_ROUTE, {
            millwrightId,
          }),
      { state: { from: `${routerLocation.pathname}${routerLocation.search}` } },
    );
  };

  return (
    <div className={classNames} {...(clickable && { onClick: handleClick })}>
      <Avatar
        {...(millwright.photoData ? { imageURL: millwright.photoData } : {})}
      />
      <div className="millwright-card__main">
        <div className="millwright-card__details">
          <Typography tag="h4">{millwright.fullName}</Typography>
          <DataItems items={dataItems} />
        </div>
        <div className="millwright-card__risk">
          <Stack gap={24}>
            <Typography tag="textdefault_strong">
              Overall Personal Risk
            </Typography>
            <SemiCircleProgressBar
              progress={millwright.globalPercentile ?? 0}
              size={isSmallScreen ? 180 : 150}
              strokeWidth={10}
              color={getRiskColor(millwright.globalPercentile ?? 0)}
              labelText={getRiskLabel(millwright.globalPercentile ?? 0)}
              mode="label"
              showLabel
            />
            {millwright.riskDate && (
              <DataItem
                key="risk"
                label="Risk Date"
                inline
                single
                value={
                  <Typography tag="textextrasmall_strong">
                    {formatDateString(millwright.riskDate, 'MMMM Do, YYYY')}
                  </Typography>
                }
              />
            )}
          </Stack>
        </div>
        {millwright.outages?.length && (
          <div className="millwright-card__outages">
            <Typography tag="textdefault_strong">Active Outages</Typography>
            <MillwrightOutages
              millwrightId={millwright.empId}
              outages={millwright.outages}
            />
          </div>
        )}
        <div className="millwright-card__info">
          <Tooltip title={MILLWRIGHT_TOOLTIP_TEXT} arrow>
            <Icon
              icon="IconInformation"
              color="var(--color-default)"
              iconSize={0.8}
            />
          </Tooltip>
        </div>
      </div>
    </div>
  );
};
